import {
  Cookie,
  FileText,
  Landmark,
  Lock,
  RotateCcw,
  Scale,
  Shield,
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { PolicyId } from '../../content/legal/types';

const POLICY_NAV: {
  id: PolicyId;
  titleKey: string;
  descKey: string;
  path: string;
  view: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  {
    id: 'privacy',
    titleKey: 'legal.privacyTitle',
    descKey: 'legal.policyDescPrivacy',
    path: '/privacy-policy',
    view: 'privacy-policy',
    icon: Lock,
  },
  {
    id: 'terms',
    titleKey: 'legal.termsTitle',
    descKey: 'legal.policyDescTerms',
    path: '/terms-of-service',
    view: 'terms-of-service',
    icon: FileText,
  },
  {
    id: 'gdpr',
    titleKey: 'legal.gdprTitle',
    descKey: 'legal.policyDescGdpr',
    path: '/gdpr-compliance',
    view: 'gdpr-compliance',
    icon: Shield,
  },
  {
    id: 'dpdp',
    titleKey: 'legal.dpdpTitle',
    descKey: 'legal.policyDescDpdp',
    path: '/dpdp-compliance',
    view: 'dpdp-compliance',
    icon: Landmark,
  },
  {
    id: 'trust',
    titleKey: 'legal.trustTitle',
    descKey: 'legal.policyDescTrust',
    path: '/trust-center',
    view: 'trust-center',
    icon: Scale,
  },
  {
    id: 'cookie',
    titleKey: 'legal.cookieTitle',
    descKey: 'legal.policyDescCookie',
    path: '/cookie-policy',
    view: 'cookie-policy',
    icon: Cookie,
  },
  {
    id: 'refund',
    titleKey: 'legal.refundTitle',
    descKey: 'legal.policyDescRefund',
    path: '/refund-cancellation',
    view: 'refund-cancellation',
    icon: RotateCcw,
  },
];

interface PolicyDocumentNavProps {
  currentPolicy: PolicyId;
  onNavigate?: (view: string) => void;
}

const PolicyDocumentNav: React.FC<PolicyDocumentNavProps> = ({ currentPolicy, onNavigate }) => {
  const { t } = useTranslation();
  const links = POLICY_NAV.filter((p) => p.id !== currentPolicy);

  const handleClick = (view: string, path: string) => {
    if (onNavigate) {
      onNavigate(view);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.location.href = path;
    }
  };

  return (
    <nav className="policy-doc-nav" aria-label={t('legal.relatedDocuments')}>
      <h2 className="policy-doc-nav__title">{t('legal.relatedDocuments')}</h2>
      <div className="policy-doc-nav__grid">
        {links.map((policy) => {
          const Icon = policy.icon;
          return (
            <button
              key={policy.id}
              type="button"
              onClick={() => handleClick(policy.view, policy.path)}
              className="policy-doc-nav__link"
            >
              <span className="policy-doc-nav__icon">
                <Icon className="w-4 h-4" />
              </span>
              <span>
                <span className="policy-doc-nav__label">{t(policy.titleKey)}</span>
                <span className="policy-doc-nav__desc block">{t(policy.descKey)}</span>
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default PolicyDocumentNav;
